/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 * 
 * ATLETAHUB — HEO-C (BLOCO 3): AGREGADOR DETERMINÍSTICO DE MÉTRICAS HTTP
 * 
 * Camada pura de agregação de métricas HTTP a partir de eventos estruturados `http.request`.
 * Agrupa métricas por [method, path_normalizado, statusClass] e calcula:
 * - total de requisições
 * - contagens de 2xx (sucesso), 4xx (client error), 5xx (server error)
 * - durações: total, média, mínima e máxima (com proteção contra NaN e divisão por zero)
 * 
 * Segurança e Isolamento:
 * - Não armazena requestId, headers, body, cookies ou credenciais.
 * - Não muta os eventos de entrada.
 */

import {
  ObservabilityEvent,
  ObservabilityEventInput,
  HttpStatusClass,
  HttpAggregateMetric,
  HttpMetricsSummary,
} from "../../types/observability";

/**
 * Arredonda valor numérico para 2 casas decimais de precisão.
 */
function round2(val: number): number {
  if (!Number.isFinite(val)) return 0;
  return Math.round(val * 100) / 100;
}

/**
 * Determina a classe de status HTTP a partir do código numérico ou string representativa.
 * Classificação canônica:
 * 100–199 → 1xx
 * 200–299 → 2xx
 * 300–399 → 3xx (incluindo 304 Not Modified)
 * 400–499 → 4xx
 * 500–599 → 5xx
 * outros / indefinidos → other
 */
export function getHttpStatusClass(statusCode?: number | string): HttpStatusClass {
  let code: number | undefined;
  if (typeof statusCode === "number" && Number.isFinite(statusCode)) {
    code = statusCode;
  } else if (typeof statusCode === "string" && statusCode.trim() !== "") {
    const parsed = Number(statusCode);
    if (Number.isFinite(parsed)) {
      code = parsed;
    }
  }

  if (code === undefined) {
    return "other";
  }
  if (code >= 100 && code < 200) return "1xx";
  if (code >= 200 && code < 300) return "2xx";
  if (code >= 300 && code < 400) return "3xx";
  if (code >= 400 && code < 500) return "4xx";
  if (code >= 500 && code < 600) return "5xx";
  return "other";
}

/**
 * Constrói a chave canônica de agrupamento para métricas HTTP.
 * Formato: METHOD|path|statusClass
 */
export function buildMetricGroupKey(
  method: string,
  path: string,
  statusClass: HttpStatusClass
): string {
  const safeMethod = (method || "UNKNOWN").toUpperCase().trim();
  const safePath = (path || "/").trim() || "/";
  return `${safeMethod}|${safePath}|${statusClass}`;
}

/**
 * Agregador determinístico de métricas de requisições HTTP.
 */
export class HttpMetricsAggregator {
  private groups: Map<string, HttpAggregateMetric> = new Map();

  /**
   * Processa um evento individual de observabilidade HTTP e acumula suas métricas.
   * Não muta o evento recebido e descarta dados sensíveis/identificadores de requisição.
   */
  public record(event: ObservabilityEvent | ObservabilityEventInput | null | undefined): void {
    if (!event || typeof event !== "object") {
      return;
    }

    // Processa EXCLUSIVAMENTE eventos com event === "http.request"
    // Qualquer outro evento (ex: database.query, auth.login) é estritamente ignorado
    if (event.event !== "http.request") {
      return;
    }

    const method = (event.method || "UNKNOWN").toUpperCase().trim();
    const rawPath = typeof event.path === "string" ? event.path.trim() : "/";
    const path = rawPath || "/";

    // Extrai statusCode de forma segura
    let statusCode: number | undefined = undefined;
    const rawStatusCode = (event as any).statusCode;
    const rawStatus = (event as any).status;

    if (typeof rawStatusCode === "number" && Number.isFinite(rawStatusCode)) {
      statusCode = rawStatusCode;
    } else if (typeof rawStatus === "number" && Number.isFinite(rawStatus)) {
      statusCode = rawStatus;
    } else if (typeof rawStatusCode === "string" && !isNaN(Number(rawStatusCode)) && rawStatusCode.trim() !== "") {
      statusCode = Number(rawStatusCode);
    } else if (typeof rawStatus === "string" && !isNaN(Number(rawStatus)) && rawStatus.trim() !== "") {
      statusCode = Number(rawStatus);
    } else if (event.status === "success") {
      statusCode = 200;
    } else if (event.status === "error") {
      statusCode = 500;
    }

    const statusClass = getHttpStatusClass(statusCode);

    // Extrai durationMs de forma segura
    let durationMs = 0;
    if (typeof event.durationMs === "number" && Number.isFinite(event.durationMs) && event.durationMs >= 0) {
      durationMs = round2(event.durationMs);
    }

    const groupKey = buildMetricGroupKey(method, path, statusClass);
    const existing = this.groups.get(groupKey);

    if (!existing) {
      const is2xx = statusCode !== undefined && statusCode >= 200 && statusCode < 300;
      const is4xx = statusCode !== undefined && statusCode >= 400 && statusCode < 500;
      const is5xx = statusCode !== undefined && statusCode >= 500 && statusCode < 600;

      const newGroup: HttpAggregateMetric = {
        method,
        path,
        statusClass,
        totalRequests: 1,
        successCount: is2xx ? 1 : 0,
        clientErrorCount: is4xx ? 1 : 0,
        serverErrorCount: is5xx ? 1 : 0,
        totalDurationMs: durationMs,
        avgDurationMs: durationMs,
        minDurationMs: durationMs,
        maxDurationMs: durationMs,
      };

      this.groups.set(groupKey, newGroup);
    } else {
      const is2xx = statusCode !== undefined && statusCode >= 200 && statusCode < 300;
      const is4xx = statusCode !== undefined && statusCode >= 400 && statusCode < 500;
      const is5xx = statusCode !== undefined && statusCode >= 500 && statusCode < 600;

      existing.totalRequests += 1;
      if (is2xx) existing.successCount += 1;
      if (is4xx) existing.clientErrorCount += 1;
      if (is5xx) existing.serverErrorCount += 1;

      existing.totalDurationMs = round2(existing.totalDurationMs + durationMs);
      existing.avgDurationMs = existing.totalRequests > 0
        ? round2(existing.totalDurationMs / existing.totalRequests)
        : 0;

      existing.minDurationMs = Math.min(existing.minDurationMs, durationMs);
      existing.maxDurationMs = Math.max(existing.maxDurationMs, durationMs);
    }
  }

  /**
   * Processa uma lista de eventos de observabilidade HTTP.
   */
  public recordMany(events: (ObservabilityEvent | ObservabilityEventInput | null | undefined)[]): void {
    if (!Array.isArray(events)) return;
    for (const ev of events) {
      this.record(ev);
    }
  }

  /**
   * Retorna cópia imutável de todas as métricas agrupadas.
   */
  public getMetrics(): HttpAggregateMetric[] {
    const list: HttpAggregateMetric[] = [];
    for (const val of Array.from(this.groups.values())) {
      list.push({ ...val });
    }
    return list;
  }

  /**
   * Retorna a métrica de um grupo específico por method, path e statusClass.
   */
  public getMetric(
    method: string,
    path: string,
    statusClass: HttpStatusClass
  ): HttpAggregateMetric | undefined {
    const groupKey = buildMetricGroupKey(method, path, statusClass);
    const metric = this.groups.get(groupKey);
    return metric ? { ...metric } : undefined;
  }

  /**
   * Retorna a quantidade de grupos distintos agregados.
   */
  public getGroupsCount(): number {
    return this.groups.size;
  }

  /**
   * Retorna o resumo global consolidado de todas as métricas agregadas na instância.
   * Não produz NaN nem Infinity caso o conjunto esteja vazio.
   */
  public getSummary(): HttpMetricsSummary {
    let totalRequests = 0;
    let successCount = 0;
    let clientErrorCount = 0;
    let serverErrorCount = 0;
    let totalDurationMs = 0;
    let minDurationMs = Number.POSITIVE_INFINITY;
    let maxDurationMs = 0;

    const groupList = Array.from(this.groups.values());

    for (const g of groupList) {
      totalRequests += g.totalRequests;
      successCount += g.successCount;
      clientErrorCount += g.clientErrorCount;
      serverErrorCount += g.serverErrorCount;
      totalDurationMs += g.totalDurationMs;

      if (g.minDurationMs < minDurationMs) {
        minDurationMs = g.minDurationMs;
      }
      if (g.maxDurationMs > maxDurationMs) {
        maxDurationMs = g.maxDurationMs;
      }
    }

    const safeTotalDuration = round2(totalDurationMs);
    const avgDurationMs = totalRequests > 0 ? round2(safeTotalDuration / totalRequests) : 0;
    const safeMin = totalRequests > 0 ? minDurationMs : 0;
    const safeMax = totalRequests > 0 ? maxDurationMs : 0;

    return {
      totalRequests,
      successCount,
      clientErrorCount,
      serverErrorCount,
      totalDurationMs: safeTotalDuration,
      avgDurationMs,
      minDurationMs: safeMin,
      maxDurationMs: safeMax,
      groupsCount: groupList.length,
    };
  }

  /**
   * Reseta todo o estado acumulado da instância.
   */
  public reset(): void {
    this.groups.clear();
  }

  /**
   * Função estática pura para agregar um array de eventos sem instanciar manualmente.
   */
  public static aggregate(
    events: (ObservabilityEvent | ObservabilityEventInput | null | undefined)[]
  ): { metrics: HttpAggregateMetric[]; summary: HttpMetricsSummary } {
    const aggregator = new HttpMetricsAggregator();
    aggregator.recordMany(events);
    return {
      metrics: aggregator.getMetrics(),
      summary: aggregator.getSummary(),
    };
  }
}

const GLOBAL_METRICS_KEY = Symbol.for("atletahub.http_metrics.aggregator");

/**
 * Retorna a instância global singleton unificada para agregação de métricas em memória.
 */
export function getGlobalHttpMetricsAggregator(): HttpMetricsAggregator {
  const globalObj = globalThis as any;
  if (!globalObj[GLOBAL_METRICS_KEY]) {
    globalObj[GLOBAL_METRICS_KEY] = new HttpMetricsAggregator();
  }
  return globalObj[GLOBAL_METRICS_KEY];
}

/**
 * Instância global singleton opcional para agregação de métricas em memória
 */
export const globalHttpMetricsAggregator = getGlobalHttpMetricsAggregator();

